import { toast } from "@/shared/hooks/useToast";
import { useMemo, useState } from "react";
import { deleteAppsflyerS3Keys, findAppsflyerS3Keys } from "../api/appsflyerRawDelete";
import type { AppsflyerRawDeleteSearchParams } from "../types/recover-types";
import { APPSFLYER_REPORT_TYPES } from "../types/recover-types";

function isValidYmd(s: string) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
    const d = new Date(`${s}T00:00:00Z`);
    return !Number.isNaN(d.getTime());
}

export function useAppsflyerRawDelete() {
    const [params, setParams] = useState<AppsflyerRawDeleteSearchParams>({
        dwAccountId: "",
        reportType: "all",
        startDate: "",
        endDate: "",
    });

    const [s3KeysToDelete, setS3KeysToDelete] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const canDelete = s3KeysToDelete.length > 0;

    const reportTypeOptions = useMemo(() => APPSFLYER_REPORT_TYPES, []);

    const findKeys = async () => {
        setIsLoading(true);
        try {
            const { dwAccountId, reportType, startDate, endDate } = params;

            if (!dwAccountId.trim()) return { ok: false as const, error: "dw_account_id를 입력해주세요." };
            if (!startDate || !endDate) return { ok: false as const, error: "조회 기간을 선택해주세요." };
            if (!isValidYmd(startDate) || !isValidYmd(endDate))
                return { ok: false as const, error: "날짜 형식이 올바르지 않습니다." };
            if (startDate > endDate) return { ok: false as const, error: "시작일이 종료일보다 클 수 없습니다." };

            const keys = await findAppsflyerS3Keys({
                dwAccountId,
                reportType,
                startDate,
                endDate,
            });

            setS3KeysToDelete(keys);
            if (keys.length === 0) {
                toast({
                    title: "파일을 찾을 수 없습니다.",
                    variant: "default",
                });
                return { ok: false as const, error: "파일을 찾을 수 없습니다." };
            }

            toast({
                title: "파일 조회 완료",
                description: `총 ${keys.length}개의 파일을 찾았습니다.`,
            });

            return { ok: true as const };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "파일 조회에 실패했습니다.";
            toast({
                title: "조회 실패",
                description: errorMessage,
                variant: "destructive",
            });
            return { ok: false as const, error: errorMessage };
        } finally {
            setIsLoading(false);
        }
    };

    const deleteKeys = async () => {
        setIsLoading(true);
        try {
            if (!canDelete) return { ok: false as const, error: "삭제할 파일이 없습니다." };

            const response = await deleteAppsflyerS3Keys(s3KeysToDelete);

            if (response.errors.length > 0) {
                return { ok: false as const, error: `삭제 과정에서 오류가 발생했습니다. 실패한 파일: ${response.errors.length}개` };
            }

            setS3KeysToDelete([]);
            setParams((p) => ({ ...p, startDate: "", endDate: "" }));

            return { ok: true as const };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "파일 삭제에 실패했습니다.";
            return { ok: false as const, error: errorMessage };
        } finally {
            setIsLoading(false);
        }
    };

    return {
        params,
        setParams,
        s3KeysToDelete,
        setS3KeysToDelete,
        isLoading,
        canDelete,
        reportTypeOptions,
        findKeys,
        deleteKeys,
    };
}
